import React from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Stack,
  useTheme,
  Chip,
} from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import { themeBackgrounds } from '../theme/theme';
import { useSelector } from 'react-redux';

interface CardItem {
  title: string;
  desc: string;
  action: string;
  icon: React.ReactElement;
}

interface ActionCardsProps {
  cards: CardItem[];
  onCardClick: (action: string) => void;
  showTour?: boolean;
  currentTourTarget?: string;
}

const ActionCards: React.FC<ActionCardsProps> = ({ 
  cards, 
  onCardClick, 
  showTour = false, 
  currentTourTarget = '' 
}) => {
  const theme = useTheme();
  const cardIds = ['create-app-card', 'setup-keyset-card', 'add-participants-card'];
  const userDetails = useSelector((state: any) => state.auth.userDetails);

  // Map card IDs to their corresponding completion status
  const getCompletionStatus = (cardId: string): boolean => {
    if (!userDetails) return false;
    
    switch (cardId) {
      case 'create-app-card':
        return userDetails.is_first_app_created || false;
      case 'setup-keyset-card':
        return userDetails.is_first_key_created || false;
      case 'add-participants-card':
        return userDetails.is_first_participant_added || false;
      default:
        return false;
    }
  };

  return (
    <Grid2 container spacing={3}>
      {cards.map((card: CardItem, i: number) => {
        const isHighlighted = showTour && currentTourTarget === cardIds[i];
        const isCompleted = getCompletionStatus(cardIds[i]);

        return (
          <Grid2 key={i} size={{ xs: 12, sm: 6, md: 4 }}>
            <Paper
              id={cardIds[i]}
              elevation={isHighlighted ? 16 : 6}
              sx={{
                height: '100%',
                p: 3,
                borderRadius: 3,
                background: themeBackgrounds.card,
                color: '#ffffff',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                transform: isHighlighted ? 'scale(1.08) translateY(-8px)' : 'scale(1)',
                zIndex: isHighlighted ? theme.zIndex.modal - 1 : 1,
                position: 'relative',
                boxShadow: isHighlighted
                  ? `0 0 0 3px ${theme.palette.warning.main}, 0 0 30px rgba(255, 152, 0, 0.4), ${theme.shadows[16]}`
                  : theme.shadows[6],
                '&::before': isHighlighted ? {
                  content: '""',
                  position: 'absolute',
                  top: -2,
                  left: -2,
                  right: -2,
                  bottom: -2,
                  background: `linear-gradient(45deg, ${theme.palette.warning.main}, ${theme.palette.warning.light}, ${theme.palette.warning.main})`,
                  borderRadius: 'inherit',
                  zIndex: -1,
                  animation: 'shimmer 2s infinite',
                  '@keyframes shimmer': {
                    '0%': { backgroundPosition: '-200% 0' },
                    '100%': { backgroundPosition: '200% 0' },
                  },
                  backgroundSize: '200% 100%',
                } : {},
                '&:hover': {
                  transform: isHighlighted ? 'scale(1.08) translateY(-8px)' : 'scale(1.02) translateY(-4px)',
                  boxShadow: isHighlighted
                    ? `0 0 0 3px ${theme.palette.warning.main}, 0 0 30px rgba(255, 152, 0, 0.4), ${theme.shadows[16]}`
                    : theme.shadows[8],
                },
              }}
            >
              {isHighlighted && (
                <>
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 10,
                      right: 10,
                      width: 8,
                      height: 8,
                      backgroundColor: theme.palette.warning.main,
                      borderRadius: '50%',
                      animation: 'sparkle 1.5s infinite',
                      '@keyframes sparkle': {
                        '0%, 100%': { opacity: 0, transform: 'scale(0)' },
                        '50%': { opacity: 1, transform: 'scale(1)' },
                      },
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 20,
                      left: 15,
                      width: 6,
                      height: 6,
                      backgroundColor: theme.palette.warning.light,
                      borderRadius: '50%',
                      animation: 'sparkle 1.5s infinite 0.5s',
                      '@keyframes sparkle': {
                        '0%, 100%': { opacity: 0, transform: 'scale(0)' },
                        '50%': { opacity: 1, transform: 'scale(1)' },
                      },
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 15,
                      right: 20,
                      width: 4,
                      height: 4,
                      backgroundColor: theme.palette.warning.main,
                      borderRadius: '50%',
                      animation: 'sparkle 1.5s infinite 1s',
                      '@keyframes sparkle': {
                        '0%, 100%': { opacity: 0, transform: 'scale(0)' },
                        '50%': { opacity: 1, transform: 'scale(1)' },
                      },
                    }}
                  />
                </>
              )}

              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <Box sx={{
                  transform: isHighlighted ? 'scale(1.2)' : 'scale(1)',
                  transition: 'transform 0.3s ease',
                }}>
                  {card.icon}
                </Box>
                <Typography variant="h6" sx={{
                  fontWeight: isHighlighted ? 'bold' : 'normal',
                  transition: 'font-weight 0.3s ease',
                  flex: 1,
                }}>
                  {card.title}
                </Typography>
                {isCompleted ? (
                  <CheckCircleIcon sx={{ fontSize: 25, color: '#3bda14ff' }} />
                ) : (
                  <Chip
                    icon={<PendingIcon sx={{ fontSize: 16, color: '#fff !important' }} />}
                    label="Pending"
                    size="small"
                    sx={{
                      backgroundColor: '#ff9800',
                      color: '#fff',
                      fontWeight: 'bold',
                      fontSize: '0.75rem',
                      height: '24px',
                      '& .MuiChip-icon': {
                        color: '#fff',
                      },
                    }}
                  />
                )}
              </Stack>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {card.desc}
              </Typography>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => onCardClick(card.action)}
                disabled={isCompleted}
                sx={{
                  borderColor: '#ffffff',
                  color: '#ffffff',
                  fontWeight: isHighlighted ? 'bold' : 'normal',
                  '&:hover': {
                    backgroundColor: '#ffffff20',
                    borderColor: '#ffffff',
                    transform: 'scale(1.02)',
                  },
                  ":disabled": {
                    borderColor: '#ffffff',
                    color: '#ffffff',
                  },
                }}
              >
                {isCompleted ? 'Completed' : card.action}
              </Button>
            </Paper>
          </Grid2>
        );
      })}
    </Grid2>
  );
};

export default ActionCards;